import axios from 'axios';
import { tansParams, blobValidate } from '@/utils/base';
import cache from '@/plugins/cache';
import { encryptBase64, encryptWithAes, generateAesKey, decryptWithAes, decryptBase64 } from '@/utils/crypto';
import { encrypt, decrypt } from '@/utils/jsencrypt';
import { Message, MessageBox } from 'element-ui';
import router from '@/router';
import store from '@/store';

const VUE_APP_LOCALSTORAGE_TOKEN_NAME = process.env.VUE_APP_LOCALSTORAGE_TOKEN_NAME;
const VUE_APP_ENCRYPT = process.env.VUE_APP_ENCRYPT;

const encryptHeader = 'encrypt-key';
const HttpStatus = {
  /**
   * 操作成功
   */
  SUCCESS: 200,
  /**
   * 对象创建成功
   */
  CREATED: 201,
  /**
   * 请求已经被接受
   */
  ACCEPTED: 202,
  /**
   * 操作已经执行成功，但是没有返回数据
   */
  NO_CONTENT: 204,
  /**
   * 资源已经被移除
   */
  MOVED_PERM: 301,
  /**
   * 重定向
   */
  SEE_OTHER: 303,
  /**
   * 资源没有被修改
   */
  NOT_MODIFIED: 304,
  /**
   * 参数列表错误（缺少，格式不匹配）
   */
  PARAM_ERROR: 400,
  /**
   * 未授权
   */
  UNAUTHORIZED: 401,
  /**
   * 访问受限，授权过期
   */
  FORBIDDEN: 403,
  /**
   * 资源，服务未找到
   */
  NOT_FOUND: 404,
  /**
   * 不允许的http方法
   */
  BAD_METHOD: 405,
  /**
   * 资源冲突，或者资源被锁
   */
  CONFLICT: 409,
  /**
   * 不支持的数据，媒体类型
   */
  UNSUPPORTED_TYPE: 415,
  /**
   * 系统内部错误
   */
  SERVER_ERROR: 500,
  /**
   * 接口未实现
   */
  NOT_IMPLEMENTED: 501,
  /**
   * 服务不可用，过载或者维护
   */
  BAD_GATEWAY: 502,
  /**
   * 网关超时
   */
  GATEWAY_TIMEOUT: 504,
  /**
   * 未知错误
   */
  UNKNOWN_ERROR: 520,
  /**
   * 服务未知错误
   */
  SERVICE_ERROR: 521,
  /**
   * 数据库未知错误
   */
  DATABASE_ERROR: 522,
  /**
   * 系统警告消息
   */
  WARN: 601
};
const errorCode = {
  401: '认证失败，无法访问系统资源',
  403: '当前操作没有权限',
  404: '访问资源不存在',
  default: '系统未知错误，请反馈给管理员'
};

// let downloadLoadingInstance: LoadingInstance;
// 是否显示重新登录
export const isRelogin = { show: false };
export const globalHeaders = () => {
  return {
    Authorization: 'Bearer ' + localStorage.getItem(VUE_APP_LOCALSTORAGE_TOKEN_NAME),
    clientid: process.env.VUE_APP_CLIENT_ID
  };
};

axios.defaults.headers['Content-Type'] = 'application/json;charset=utf-8';
axios.defaults.headers['clientid'] = process.env.VUE_APP_CLIENT_ID;
// 创建 axios 实例
const service = axios.create({
  baseURL: process.env.VUE_APP_BASE_API,
  timeout: 50000
});

// 请求拦截器
service.interceptors.request.use(
  (config) => {
    // 对应国际化资源文件后缀
    config.headers['Content-Language'] = 'zh_CN';

    const isToken = config.headers?.isToken === false;
    // 是否需要防止数据重复提交
    const isRepeatSubmit = config.headers?.repeatSubmit === false;
    // 是否需要加密
    const isEncrypt = config.headers?.isEncrypt === 'true';

    if (localStorage.getItem(VUE_APP_LOCALSTORAGE_TOKEN_NAME) && !isToken) {
      config.headers['Authorization'] = 'Bearer ' + localStorage.getItem(VUE_APP_LOCALSTORAGE_TOKEN_NAME); // 让每个请求携带自定义token 请根据实际情况自行修改
    }
    // get请求映射params参数
    if (config.method === 'get' && config.params) {
      let url = config.url + '?' + tansParams(config.params);
      url = url.slice(0, -1);
      config.params = {};
      config.url = url;
    }

    if (!isRepeatSubmit && (config.method === 'post' || config.method === 'put')) {
      const requestObj = {
        url: config.url,
        data: typeof config.data === 'object' ? JSON.stringify(config.data) : config.data,
        time: new Date().getTime()
      };
      const sessionObj = cache.session.getJSON('sessionObj');
      if (sessionObj === undefined || sessionObj === null || sessionObj === '') {
        cache.session.setJSON('sessionObj', requestObj);
      } else {
        const s_url = sessionObj.url; // 请求地址
        const s_data = sessionObj.data; // 请求数据
        const s_time = sessionObj.time; // 请求时间
        const interval = 500; // 间隔时间(ms)，小于此时间视为重复提交
        if (s_data === requestObj.data && requestObj.time - s_time < interval && s_url === requestObj.url) {
          const message = '数据正在处理，请勿重复提交';
          console.warn(`[${s_url}]: ` + message);
          return Promise.reject(new Error(message));
        } else {
          cache.session.setJSON('sessionObj', requestObj);
        }
      }
    }
    if (VUE_APP_ENCRYPT === 'true') {
      // 当开启参数加密
      if (isEncrypt && (config.method === 'post' || config.method === 'put')) {
        // 生成一个 AES 密钥
        const aesKey = generateAesKey();
        config.headers[encryptHeader] = encrypt(encryptBase64(aesKey));
        config.data =
          typeof config.data === 'object'
            ? encryptWithAes(JSON.stringify(config.data), aesKey)
            : encryptWithAes(config.data, aesKey);
      }
    }
    // FormData数据去请求头Content-Type
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// 响应拦截器
service.interceptors.response.use(
  (res) => {
    if (VUE_APP_ENCRYPT === 'true') {
      // 加密后的 AES 秘钥
      const keyStr = res.headers[encryptHeader];
      // 加密
      if (keyStr != null && keyStr != '') {
        const data = res.data;
        // 请求体 AES 解密
        const base64Str = decrypt(keyStr);
        // base64 解码 得到请求头的 AES 秘钥
        const aesKey = decryptBase64(base64Str.toString());
        // aesKey 解码 data
        const decryptData = decryptWithAes(data, aesKey);
        // 将结果 (得到的是 JSON 字符串) 转为 JSON
        res.data = JSON.parse(decryptData);
      }
    }
    // 未设置状态码则默认成功状态
    const code = res.data.code || HttpStatus.SUCCESS;
    // 获取错误信息
    const msg = errorCode[code] || res.data.msg || res.msg || errorCode['default'];
    // 二进制数据则直接返回
    if (res.request.responseType === 'blob' || res.request.responseType === 'arraybuffer') {
      return res.data;
    }
    if (code === 401) {
      // prettier-ignore
      if (!isRelogin.show) {
        isRelogin.show = true;
        MessageBox.confirm('登录状态已过期，您可以继续留在该页面，或者重新登录', '系统提示', {
          confirmButtonText: '重新登录',
          cancelButtonText: '取消',
          type: 'warning'
        }).then(async () => {
          isRelogin.show = false;
            await store.dispatch('login/logout');
            router.replace({
              path: '/login',
              // query: {
              //   redirect: encodeURIComponent(router.currentRoute.value.fullPath || '/')
              // }
            }).catch(err => {})
        }).catch(() => {
          isRelogin.show = false;
        });
      }
      // return Promise.reject('无效的会话，或者会话已过期，请重新登录。');
    } else if (code === HttpStatus.SERVER_ERROR) {
      Message.error({ message: msg });
      return Promise.reject(new Error(msg));
    } else if (code === HttpStatus.WARN) {
      Message.warning({ message: msg });
      return Promise.reject(new Error(msg));
    } else if (code !== HttpStatus.SUCCESS) {
      Message.error({ title: msg });
      return Promise.reject('error');
    } else {
      return Promise.resolve(res.data);
    }
  },
  (error) => {
    let { message } = error;
    if (message == 'Network Error') {
      message = '后端接口连接异常';
    } else if (message.includes('timeout')) {
      message = '系统接口请求超时';
    } else if (message.includes('Request failed with status code')) {
      message = '系统接口' + message.substr(message.length - 3) + '异常';
    }
    Message.error({ message: message, duration: 5 * 1000 });
    return Promise.reject(error);
  }
);

// 通用下载方法
export function download(url, params, fileName) {
  // downloadLoadingInstance = ElLoading.service({ text: '正在下载数据，请稍候', background: 'rgba(0, 0, 0, 0.7)' });
  // prettier-ignore
  return service.post(url, params, {
      transformRequest: [
        (params) => {
          return tansParams(params);
        }
      ],
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      responseType: 'blob'
    }).then(async (resp) => {
      const isLogin = blobValidate(resp);
      if (isLogin) {
        const blob = new Blob([resp]);
        FileSaver.saveAs(blob, fileName);
      } else {
        const resText = await resp.data.text();
        const rspObj = JSON.parse(resText);
        const errMsg = errorCode[rspObj.code] || rspObj.msg || errorCode['default'];
        Message.error(errMsg);
      }
      // downloadLoadingInstance.close();
    }).catch((r) => {
      console.error(r);
      Message.error('下载文件出现错误，请联系管理员！');
      // downloadLoadingInstance.close();
    });
}

// 导出 axios 实例
export default service;
